<script setup lang="ts">
    import {ref,computed,watch} from 'vue';
    import CpModal from '~/components/cp/CpModal/CpModal.vue';
    import CpSectionHeader from '~/components/cp/CpSection/CpSectionHeader/CpSectionHeader.vue';
    import CpSectionPdpWarrantyModule from '~/components/cp/CpSectionPdpWarrantyModule/CpSectionPdpWarrantyModule.vue';
    import CpButton from '~/components/cp/CpButton/CpButton.vue';
    import CpText from '~/components/cp/CpText/CpText.vue';
    import {useI18n} from 'vue-i18n';
    const {t} = useI18n();

    enum WarrantyKind {
        Brand = 'Brand',
        FolioRequired = 'FolioRequired',
        Cp = 'Cp',
        Provider = 'Provider'
    }
    
    const props = defineProps({
        show: {
            type: Boolean,
            required: true,
        },
        persistent: {
            type: Boolean,
            required: true,
        },
        brandName: {
            type: String,
            required: true,
        },
        kind: {
            type: String,
            required: true,
            validator: (value:string)=>['Brand', 'FolioRequired', 'Cp', 'Provider'].includes(value),
        },
        warrantyTime: {
            type: Number,
            required: false,
            default: 1
        },
        hrefFirstButton: {
            type: String,
        }
    });

    const emit = defineEmits<{
        (e: 'update:show', value: boolean): void,
    }>();

    const value = computed({
        get() {
            return props.show;
        },
        set(value) {
            emit('update:show', value);
        },
    });
    const arrayPharagraph = (text)=>{
        let list: Array<string> = text.split('\n');
        list = list.filter((str)=>(str!='')?str:null);
        return list;
    };
    const warrantyParagraphs= computed(()=>{
       switch(props.kind){
        case WarrantyKind.Brand:
            return arrayPharagraph(t('warranty-brand-body',{brandName:(props.kind=='Cp') ?'Cyberpuerta':props.brandName, count: props.warrantyTime}));
        case WarrantyKind.FolioRequired:
            return arrayPharagraph(t('warranty-folio-required-body',{count: props.warrantyTime}));
        case WarrantyKind.Cp:
            return arrayPharagraph(t('warranty-cp-body',{count: props.warrantyTime}));
        case WarrantyKind.Provider:
            return arrayPharagraph(t('warranty-provider-body',{brandName: props.brandName, count: props.warrantyTime}));
    }
    });
</script>

<template>
    <CpModal v-model:show="value" :persistent="persistent" @close-modal="value = false">
       
        <CpSectionHeader
                title="Tipo de garantía del producto"
                button="close"
                type="h3"
                @header-action="value = false"
                />
                <div class="cp-modal-warranty-module">
                <div class="cp-modal-warranty-module__content">
                    <CpSectionPdpWarrantyModule :brand-name="(kind=='Cp')?'Cyberpuerta':brandName" :warranty-time="warrantyTime" :kind="kind" :show-link="false"/>
                </div>
                <div class="cp-modal-warranty-module__text">
                    <ul>
                        <li v-for="paragraph in warrantyParagraphs" :key="paragraph">
                            <CpText variant="body-regular">{{ paragraph }}</CpText>
                        </li>
                    </ul>
                </div>
            </div>
            <div class="cp-modal-warranty-module__buttons">
                    <CpButton
                        color="primary"
                        label="Conocer más acerca de garantías"
                        kind="label"
                        full-width
                        :href="hrefFirstButton"
                        :target="targetFirstButton"
                        :data-testid="firstButtonTestId ? firstButtonTestId : 'first-button-dialog'"
                        @click="$emit('first-button')"
                    />
                </div>
        </CpModal>
    
</template>

<style lang="scss" scoped>
    ul li::marker {
        font-size: 12px;
    }
    .cp-modal-warranty-module {
        width: 100%;
        max-width: 350px;
        display: flex;
        flex-direction: column;
        align-items: center;
        overflow: hidden;
        align-self: center;
        //padding: 16px;
    }
    .cp-modal-warranty-module__text{
        gap: 8px;
        ul {
            padding-left: var(--cp-gutter);
            padding-bottom: var(--cp-gutter);
        }
    }
    :deep(div.cp-section-pdp-warranty-module__container) {
        border-top: none;
        padding-bottom: var(--cp-gutter-small);
    }
    .cp-modal-warranty-module__buttons{
        border-top: 1px solid var(--color-neutral-extra-light);
        padding: var(--cp-gutter);
    }
</style>
<i18n locale="es-US" lang="yaml">
    warranty-brand: "¡Tu producto tiene garantía con {brandName} por {count} año! | ¡Tu producto tiene garantía con {brandName} por {count} años!"
    warranty-folio-required: "¡Tu producto tiene garantía por {count} año! | ¡Tu producto tiene garantía por {count} años!"
    warranty-cp: "¡La garantía de tu producto está a cargo de Cyberpuerta por {count} año! | ¡La garantía de tu producto está a cargo de Cyberpuerta por {count} años!"
    warranty-provider: "¡Tu producto tiene garantía con un Centro de Servicio Especializado en {brandName} por {count} año! | ¡Tu producto tiene garantía con un Centro de Servicio Especializado en {brandName} por {count} años!"
    warranty-brand-body: |
        Trato directo con {brandName}, sin necesidad de intermediarios.
        Técnicos dedicados a identificar la falla en el menor tiempo posible.
        Recibe respuestas precisas y confiables de expertos en la marca de tu producto.
        Si se determina que tu producto necesita reparación, esta se realizará a la brevedad, sin necesidad de reemplazo.
    warranty-folio-required-body: |
        Deberás contactar a {brandName}, cuyos técnicos especializados realizarán pruebas preliminares de fallas en tu producto.
        Comparte con nosotros el folio proporcionado por {brandName}. Estamos comprometidos a dar seguimiento de tu garantía, que será realizado por un centro de servicio especializado en {brandName}. 
        Si se determina que tu producto necesita reparación, esta se realizará a la brevedad, sin necesidad de reemplazo.
        El Centro de Servicio Especializado, evaluará tu producto para determinar si procede la reparación, reembolso o el reemplazo de tu producto.
        Por favor, toma en cuenta que en este caso los tiempos de respuesta pueden ser mayores, debido a que la garantía se efectúa a través de un centro de servicio especializado y no a través de la misma marca o Cyberpuerta.
    warranty-cp-body: |
        Tiempo de respuesta rápido, gracias a la comunicación directa con nosotros.
        Confianza y seguridad en el proceso, te proporcionamos pruebas directas de nuestros expertos técnicos en diagnóstico.
        Estamos autorizados como centro de servicio por el fabricante de tu producto.
        Seguimos estrictos protocolos que aseguran la detección de fallas en tu producto.
    warranty-provider-body: |
        Las garantías de {brandName} se realizan por medio de un Centro de Servicio Especializado.
        Con la finalidad de brindarte un servicio excepcional, te apoyaremos con el seguimiento a la solicitud de tu garantía.
        El Centro de Servicio Especializado, evaluará tu producto para determinar si procede la reparación, reembolso o el reemplazo de tu producto.
        Por favor, toma en cuenta que en este caso los tiempos de respuesta pueden ser mayores, debido a que la garantía se efectúa a través de un centro de servicio especializado y no a través de la misma marca o Cyberpuerta.
</i18n>